import React, { useState, useEffect } from 'react';
import { EntityRequirement } from '../CustomHooks/useFetchRequirements';
import styles from '../NeedInfoV2.module.scss';
import { Input } from '../../_custom';
import Button from '../../_custom/Button/Button';
import EmailPreview from './EmailPreview';
import { useAppSelector } from '../../../app/hooks';

interface AddClarificationDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRequirements: EntityRequirement[];
    onSubmit: (clarification: string[], requirementIds: string[]) => void;
}

const ReviewEmailDrawer: React.FC<AddClarificationDrawerProps> = ({ isOpen, onClose, selectedRequirements, onSubmit }) => {
    const [clarification, setClarification] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const logoUrl = useAppSelector((state) => state.user.domainMetadata?.logoUrl);

    // Default email template
    const [emailTemplate, setEmailTemplate] = useState({
        subject: 'Pending Data and Clarifications for {{companyName}}',
        body: `
  <div style="font-family: Arial, sans-serif; margin: 0; padding: 20px; background-color: #ffffff; color: #000000;">
    <div style="width: 100%; max-width: 720px; margin: 0 auto;">
      <!-- Logo -->
      <div style="margin-bottom: 20px;">
        <img src="{{logoUrl}}" alt="Company Logo" width="100" />
      </div>

      <p>Hi All,</p>

      <p>Please find pending data and clarifications for <strong>{{companyName}}</strong>:</p>

      <h3>Pending List:</h3>

      <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
        <thead>
          <tr>
            <th style="border: 1px solid #cccccc; padding: 10px; font-size: 14px; vertical-align: top; text-align: left; background-color: #f2f2f2;">Data Type</th>
            <th style="border: 1px solid #cccccc; padding: 10px; font-size: 14px; vertical-align: top; text-align: left; background-color: #f2f2f2;">List of Data and Clarification</th>
            <th style="border: 1px solid #cccccc; padding: 10px; font-size: 14px; vertical-align: top; text-align: left; background-color: #f2f2f2;">Status</th>
          </tr>
        </thead>
        <tbody>
          {{pendingItemsTable}}
        </tbody>
      </table>

      <p>Simply reply to this email with further data and clarifications.</p>

      <p>Thanks,<br/>
      AICA User</p>

      <div style="margin-top: 30px; font-size: 12px; color: #999999;">
        Not you? Please report by replying to this email immediately.
      </div>
    </div>
  </div>`,
    });

    // Generate variables for email template
    const getEmailVariables = () => {
        // Generate table rows for pending items
        const pendingItemsRows = selectedRequirements
            .map(
                (req) =>
                    `<tr>
                <td style="border: 1px solid #cccccc;"><strong>${req.dataType}</strong></td>
                <td style="border: 1px solid #cccccc;">${req.primaryMessage}</td>
                <td style="border: 1px solid #cccccc;" class="status-pending">${req.status || 'Pending'}</td>
            </tr>`,
            )
            .join('');

        // Add the clarification as a separate row if provided
        const clarificationRow = clarification
            .map((item) => item.trim())
            .filter(Boolean)
            .map(
                (item) =>
                    `<tr>
                    <td style="border: 1px solid #cccccc;"><strong>Clarification</strong></td>
                    <td style="border: 1px solid #cccccc;">${item}</td>
                    <td style="border: 1px solid #cccccc;" class="status-pending">Pending</td>
                </tr>`,
            )
            .join('');

        // Combine all rows
        const allRows = pendingItemsRows + clarificationRow;

        return [
            { key: 'logoUrl', value: logoUrl ?? '' },
            { key: 'companyName', value: 'Your Company' }, // This should be dynamically populated in a real scenario
            { key: 'pendingItemsTable', value: allRows || '<tr><td colspan="3">No requirements selected</td></tr>' },
            { key: 'requirementCount', value: selectedRequirements.length.toString() }, // Keep backward compatibility
            { key: 'clarificationText', value: clarification.length > 0 ? clarification.join('<br/>') : 'No clarification provided yet' }, // Keep backward compatibility
        ];
    };

    useEffect(() => {
        if (!isOpen) {
            setClarification([]);
        }
    }, [isOpen]);

    // Handle input change and convert to array based on line breaks
    const handleClarificationChange = (value: string) => {
        const lines = value.split('\n');
        setClarification(lines);
    };

    const handleSubmit = async () => {
        // Check if there's any non-empty clarification after trimming
        const hasValidClarification = clarification.some((line) => line.trim().length > 0);
        if (!hasValidClarification) return;

        setIsSubmitting(true);

        try {
            const requirementIds = selectedRequirements.map((req) => req.id);
            onSubmit(clarification, requirementIds as string[]);
            setClarification([]);
            onClose();
        } catch (error) {
            console.error('Error submitting clarification:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <div
                className={`${styles.Backdrop} ${isOpen ? styles.BackdropVisible : ''}`}
                onClick={onClose}
                role="presentation"
                aria-hidden={!isOpen}
            />
            <div
                className={`${styles.Drawer} ${isOpen ? styles.Open : styles.Closed}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="clarification-drawer-title"
            >
                <div className={styles.Header}>
                    <h2 id="clarification-drawer-title" className={styles.HeaderText}>
                        Send/Review Email
                    </h2>
                    <button onClick={onClose} className={styles.CloseButton} aria-label="Close drawer">
                        ×
                    </button>
                </div>
                <div className={styles.Content}>
                    <div className={styles.DrawerContainer}>
                        {/* Left Panel - Clarification Input */}
                        <div className={styles.LeftPanel} style={{ display: 'flex', flexDirection: 'column' }}>
                            <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
                                <h3 style={{ marginBottom: '1rem', fontSize: '1rem' }}>Selected Requirements:</h3>
                                <ul style={{ marginBottom: '1.5rem', paddingLeft: '1.5rem' }}>
                                    {selectedRequirements.map((req) => (
                                        <li key={req.id} style={{ marginBottom: '0.5rem' }}>
                                            {req.primaryMessage}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div style={{ marginBottom: '1rem', marginTop: 'auto' }}>
                                <Input
                                    type="textarea"
                                    placeholder="Enter your clarification..."
                                    value={clarification.join('\n')}
                                    onChange={handleClarificationChange}
                                    textAreaRows={6}
                                />
                            </div>

                            <div
                                style={{
                                    borderTop: '1px solid #e0e0e0',
                                    padding: '1rem 0',
                                    marginTop: '0.5rem',
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    gap: '1rem',
                                }}
                            >
                                <Button text="Cancel" onClick={onClose} variant="secondary" style={{ minWidth: '100px', margin: 0 }} />
                                <Button
                                    text={isSubmitting ? 'Submitting...' : 'Submit Clarification'}
                                    onClick={handleSubmit}
                                    variant="primary"
                                    style={{ minWidth: '150px', margin: 0 }}
                                    disabled={
                                        !clarification.some((line) => line.trim().length > 0) ||
                                        isSubmitting ||
                                        selectedRequirements.length === 0
                                    }
                                />
                            </div>
                        </div>

                        {/* Right Panel - Email Preview */}
                        <div className={styles.RightPanel}>
                            <EmailPreview subject={emailTemplate.subject} body={emailTemplate.body} variables={getEmailVariables()} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ReviewEmailDrawer;
